// public/indexeddb-cache.js
// IndexedDB-based cache manager for binder data
// Provides stale-while-revalidate pattern with ETag validation

const DB_NAME = 'binderBrowserCache';
const DB_VERSION = 1;
const CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes
const MAX_CACHE_SIZE_MB = 50;
const MAX_CACHE_SIZE_BYTES = MAX_CACHE_SIZE_MB * 1024 * 1024;

// Store names
const STORES = {
  BINDERS: 'binders',
  COLLECTION_PAGES: 'collectionPages',
  COLLECTION_META: 'collectionMeta',
  METADATA: 'cacheMetadata',
};

// Current data version for migrations
const DATA_VERSION = 1;

// Database instance (lazy initialized)
let dbInstance = null;
let dbInitPromise = null;
let idbAvailable = true;

/**
 * Initialize the IndexedDB database
 * @returns {Promise<IDBDatabase|null>} Database instance or null if unavailable
 */
export async function initCacheDB() {
  // Return existing instance if available
  if (dbInstance) {
    return dbInstance;
  }

  // Return existing init promise if in progress
  if (dbInitPromise) {
    return dbInitPromise;
  }

  dbInitPromise = new Promise((resolve) => {
    try {
      if (!window.indexedDB) {
        console.warn('[Cache] IndexedDB not available');
        idbAvailable = false;
        resolve(null);
        return;
      }

      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('[Cache] Failed to open IndexedDB:', request.error);
        idbAvailable = false;
        resolve(null);
      };

      request.onsuccess = () => {
        dbInstance = request.result;
        console.log('[Cache] IndexedDB initialized successfully');

        // Handle connection errors
        dbInstance.onerror = (event) => {
          // IDB error events bubble up from the failing request.
          const failed = /** @type {IDBRequest} */ (event.target);
          console.error('[Cache] Database error:', failed.error);
        };

        resolve(dbInstance);
      };

      request.onupgradeneeded = () => {
        const db = request.result;
        console.log('[Cache] Upgrading IndexedDB schema...');

        // Binders store - for non-paginated decks
        if (!db.objectStoreNames.contains(STORES.BINDERS)) {
          const bindersStore = db.createObjectStore(STORES.BINDERS, { keyPath: 'cacheKey' });
          bindersStore.createIndex('fetchedAt', 'fetchedAt', { unique: false });
          bindersStore.createIndex('simpleUrl', 'simpleUrl', { unique: false });
        }

        // Collection pages store - for paginated content
        if (!db.objectStoreNames.contains(STORES.COLLECTION_PAGES)) {
          const pagesStore = db.createObjectStore(STORES.COLLECTION_PAGES, { keyPath: 'pageKey' });
          pagesStore.createIndex('collectionKey', 'collectionKey', { unique: false });
          pagesStore.createIndex('fetchedAt', 'fetchedAt', { unique: false });
        }

        // Collection metadata store
        if (!db.objectStoreNames.contains(STORES.COLLECTION_META)) {
          const metaStore = db.createObjectStore(STORES.COLLECTION_META, {
            keyPath: 'collectionKey',
          });
          metaStore.createIndex('fetchedAt', 'fetchedAt', { unique: false });
        }

        // Global cache metadata
        if (!db.objectStoreNames.contains(STORES.METADATA)) {
          db.createObjectStore(STORES.METADATA, { keyPath: 'key' });
        }

        console.log('[Cache] IndexedDB schema created');
      };
    } catch (error) {
      console.error('[Cache] Error initializing IndexedDB:', error);
      idbAvailable = false;
      resolve(null);
    }
  });

  return dbInitPromise;
}

/**
 * Get database instance, initializing if needed
 * @returns {Promise<IDBDatabase|null>}
 */
async function getDB() {
  if (!idbAvailable) return null;
  if (dbInstance) return dbInstance;
  return initCacheDB();
}

/**
 * Generate cache key from URL components
 * @param {string} source - Source name (moxfield, archidekt, scryfall)
 * @param {string} type - Type (deck, binder, collection)
 * @param {string} id - Unique identifier
 * @returns {string} Cache key
 */
export function getCacheKey(source, type, id) {
  return `${source}:${type}:${id}`;
}

/**
 * Generate page cache key
 * @param {string} source
 * @param {string} type
 * @param {string} id
 * @param {string} sortMode
 * @param {string|null} colorFilter
 * @param {number} page
 * @returns {string}
 */
export function getPageCacheKey(source, type, id, sortMode, colorFilter, page) {
  const color = colorFilter || 'all';
  return `${source}:${type}:${id}:${sortMode}:${color}:${page}`;
}

/**
 * Check if data is stale (older than TTL)
 * @param {number} fetchedAt - Timestamp when data was fetched
 * @returns {boolean}
 */
export function isStale(fetchedAt) {
  return Date.now() - fetchedAt > CACHE_TTL_MS;
}

/**
 * Get cached binder data if available
 * @param {string} cacheKey - Cache key from getCacheKey()
 * @returns {Promise<{data: Object, etag: string, isStale: boolean}|null>}
 */
export async function getCachedBinder(cacheKey) {
  const db = await getDB();
  if (!db) return null;

  return new Promise((resolve) => {
    try {
      const transaction = db.transaction([STORES.BINDERS], 'readonly');
      const store = transaction.objectStore(STORES.BINDERS);
      const request = store.get(cacheKey);

      request.onsuccess = () => {
        const record = request.result;
        if (!record) {
          resolve(null);
          return;
        }

        resolve({
          data: {
            name: record.name,
            owner: record.owner,
            simple_url: record.simpleUrl,
            cards: record.cards,
          },
          etag: record.etag,
          isStale: isStale(record.fetchedAt),
        });
      };

      request.onerror = () => {
        console.error('[Cache] Error reading binder:', request.error);
        resolve(null);
      };
    } catch (error) {
      console.error('[Cache] Error in getCachedBinder:', error);
      resolve(null);
    }
  });
}

/**
 * Store binder data in cache
 * @param {string} cacheKey - Cache key
 * @param {Object} data - Binder data from API
 * @param {string} etag - ETag from server
 * @returns {Promise<boolean>} Success status
 */
export async function cacheBinder(cacheKey, data, etag) {
  const db = await getDB();
  if (!db) return false;

  return new Promise((resolve) => {
    try {
      const transaction = db.transaction([STORES.BINDERS], 'readwrite');
      const store = transaction.objectStore(STORES.BINDERS);

      const record = {
        cacheKey,
        etag,
        fetchedAt: Date.now(),
        name: data.name,
        owner: data.owner,
        simpleUrl: data.simple_url,
        cards: data.cards,
        totalCards: data.cards?.length || 0,
        dataVersion: DATA_VERSION,
      };

      const request = store.put(record);

      request.onsuccess = () => {
        console.log(`[Cache] Cached binder: ${cacheKey}`);
        resolve(true);
        // Check if eviction needed (async, don't wait)
        performEvictionIfNeeded();
      };

      request.onerror = () => {
        console.error('[Cache] Error caching binder:', request.error);
        resolve(false);
      };
    } catch (error) {
      console.error('[Cache] Error in cacheBinder:', error);
      resolve(false);
    }
  });
}

/**
 * Get cached collection page
 * @param {string} pageKey - Page cache key from getPageCacheKey()
 * @returns {Promise<{data: Object, etag: string, isStale: boolean}|null>}
 */
export async function getCachedCollectionPage(pageKey) {
  const db = await getDB();
  if (!db) return null;

  return new Promise((resolve) => {
    try {
      const transaction = db.transaction([STORES.COLLECTION_PAGES], 'readonly');
      const store = transaction.objectStore(STORES.COLLECTION_PAGES);
      const request = store.get(pageKey);

      request.onsuccess = () => {
        const record = request.result;
        if (!record) {
          resolve(null);
          return;
        }

        resolve({
          data: {
            cards: record.cards,
            totalCards: record.totalCards,
            hasMore: record.hasMore,
            sessionId: record.sessionId,
          },
          etag: record.etag,
          isStale: isStale(record.fetchedAt),
        });
      };

      request.onerror = () => {
        console.error('[Cache] Error reading collection page:', request.error);
        resolve(null);
      };
    } catch (error) {
      console.error('[Cache] Error in getCachedCollectionPage:', error);
      resolve(null);
    }
  });
}

/**
 * Store collection page in cache
 * @param {string} pageKey - Page cache key
 * @param {string} collectionKey - Parent collection key
 * @param {Object} data - Page data from API
 * @param {string} etag - ETag from server
 * @returns {Promise<boolean>}
 */
export async function cacheCollectionPage(pageKey, collectionKey, data, etag) {
  const db = await getDB();
  if (!db) return false;

  return new Promise((resolve) => {
    try {
      const transaction = db.transaction([STORES.COLLECTION_PAGES], 'readwrite');
      const store = transaction.objectStore(STORES.COLLECTION_PAGES);

      const record = {
        pageKey,
        collectionKey,
        etag,
        fetchedAt: Date.now(),
        cards: data.cards,
        totalCards: data.totalCards,
        hasMore: data.hasMore,
        sessionId: data.sessionId,
        dataVersion: DATA_VERSION,
      };

      const request = store.put(record);

      request.onsuccess = () => {
        console.log(`[Cache] Cached collection page: ${pageKey}`);
        resolve(true);
        performEvictionIfNeeded();
      };

      request.onerror = () => {
        console.error('[Cache] Error caching collection page:', request.error);
        resolve(false);
      };
    } catch (error) {
      console.error('[Cache] Error in cacheCollectionPage:', error);
      resolve(false);
    }
  });
}

/**
 * Get collection metadata (which pages are cached, etc.)
 * @param {string} collectionKey
 * @returns {Promise<Object|null>}
 */
export async function getCollectionMeta(collectionKey) {
  const db = await getDB();
  if (!db) return null;

  return new Promise((resolve) => {
    try {
      const transaction = db.transaction([STORES.COLLECTION_META], 'readonly');
      const store = transaction.objectStore(STORES.COLLECTION_META);
      const request = store.get(collectionKey);

      request.onsuccess = () => {
        resolve(request.result || null);
      };

      request.onerror = () => {
        console.error('[Cache] Error reading collection meta:', request.error);
        resolve(null);
      };
    } catch (error) {
      console.error('[Cache] Error in getCollectionMeta:', error);
      resolve(null);
    }
  });
}

/**
 * Update collection metadata
 * @param {string} collectionKey
 * @param {Object} meta - Metadata to store
 * @returns {Promise<boolean>}
 */
export async function updateCollectionMeta(collectionKey, meta) {
  const db = await getDB();
  if (!db) return false;

  return new Promise((resolve) => {
    try {
      const transaction = db.transaction([STORES.COLLECTION_META], 'readwrite');
      const store = transaction.objectStore(STORES.COLLECTION_META);

      const record = {
        collectionKey,
        ...meta,
        fetchedAt: Date.now(),
        dataVersion: DATA_VERSION,
      };

      const request = store.put(record);

      request.onsuccess = () => {
        resolve(true);
      };

      request.onerror = () => {
        console.error('[Cache] Error updating collection meta:', request.error);
        resolve(false);
      };
    } catch (error) {
      console.error('[Cache] Error in updateCollectionMeta:', error);
      resolve(false);
    }
  });
}

/**
 * Get cached initial collection data (combines first pages of price and color sorts)
 * @param {string} collectionKey
 * @returns {Promise<{data: Object, etag: string, isStale: boolean}|null>}
 */
export async function getCachedCollectionInitial(collectionKey) {
  const db = await getDB();
  if (!db) return null;

  // Get collection metadata first
  const meta = await getCollectionMeta(collectionKey);
  if (!meta || !meta.etag) return null;

  // Parse collection key to get components
  const [source, type, id] = collectionKey.split(':');

  // Get first price page
  const pricePageKey = getPageCacheKey(source, type, id, 'price', null, 1);
  const pricePage = await getCachedCollectionPage(pricePageKey);

  // Get first color page (white)
  const colorPageKey = getPageCacheKey(source, type, id, 'color', 'w', 1);
  const colorPage = await getCachedCollectionPage(colorPageKey);

  if (!pricePage || !colorPage) return null;

  // Check staleness based on oldest data
  const oldestFetchedAt = Math.min(
    meta.fetchedAt || 0,
    pricePage.isStale ? 0 : Date.now(),
    colorPage.isStale ? 0 : Date.now()
  );

  return {
    data: {
      name: meta.name,
      owner: meta.owner,
      simple_url: meta.simpleUrl,
      isCollection: true,
      collectionType: type,
      source,
      sourceId: id,
      totalCards: meta.totalCards,
      initialPriceCards: pricePage.data.cards,
      initialColorCards: colorPage.data.cards,
      priceHasMore: pricePage.data.hasMore,
      colorHasMore: colorPage.data.hasMore,
      // Include ID fields for pagination
      collectionId: meta.collectionId,
      userId: meta.userId,
    },
    etag: meta.etag,
    isStale: isStale(oldestFetchedAt),
  };
}

/**
 * Cache initial collection data (first pages of both sorts + metadata)
 * @param {string} collectionKey
 * @param {Object} data - Initial collection data from API
 * @param {string} etag - ETag from server
 * @returns {Promise<boolean>}
 */
export async function cacheCollectionInitial(collectionKey, data, etag) {
  const db = await getDB();
  if (!db) return false;

  const [source, type, id] = collectionKey.split(':');

  // Cache the price page
  const pricePageKey = getPageCacheKey(source, type, id, 'price', null, 1);
  await cacheCollectionPage(
    pricePageKey,
    collectionKey,
    {
      cards: data.initialPriceCards,
      totalCards: data.totalCards,
      hasMore: data.priceHasMore,
    },
    etag
  );

  // Cache the color page
  const colorPageKey = getPageCacheKey(source, type, id, 'color', 'w', 1);
  await cacheCollectionPage(
    colorPageKey,
    collectionKey,
    {
      cards: data.initialColorCards,
      totalCards: data.totalCards,
      hasMore: data.colorHasMore,
    },
    etag
  );

  // Cache collection metadata
  await updateCollectionMeta(collectionKey, {
    etag,
    name: data.name,
    owner: data.owner,
    simpleUrl: data.simple_url,
    totalCards: data.totalCards,
    collectionId: data.collectionId,
    userId: data.userId,
    cachedPages: {
      price: [1],
      color: { w: [1], u: [], b: [], r: [], g: [], c: [], multi: [] },
    },
  });

  return true;
}

/**
 * Estimate total cache size in bytes
 * @returns {Promise<number>}
 */
async function estimateCacheSize() {
  const db = await getDB();
  if (!db) return 0;

  let totalSize = 0;

  const storeNames = [STORES.BINDERS, STORES.COLLECTION_PAGES, STORES.COLLECTION_META];

  for (const storeName of storeNames) {
    try {
      const size = await new Promise((resolve) => {
        const transaction = db.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);
        const request = store.getAll();

        request.onsuccess = () => {
          const records = request.result || [];
          const jsonSize = JSON.stringify(records).length * 2; // UTF-16
          resolve(jsonSize);
        };

        request.onerror = () => resolve(0);
      });
      totalSize += size;
    } catch (error) {
      console.error(`[Cache] Error estimating size for ${storeName}:`, error);
    }
  }

  return totalSize;
}

/**
 * Perform LRU eviction if cache exceeds size limit
 * @param {boolean} force - Force eviction even if under limit
 * @returns {Promise<void>}
 */
export async function performEvictionIfNeeded(force = false) {
  const db = await getDB();
  if (!db) return;

  try {
    const currentSize = await estimateCacheSize();
    const threshold = force ? 0 : MAX_CACHE_SIZE_BYTES * 0.8;

    if (currentSize < threshold) {
      return;
    }

    console.log(
      `[Cache] Starting eviction (${(currentSize / 1024 / 1024).toFixed(2)}MB / ${MAX_CACHE_SIZE_MB}MB)`
    );

    // Target: free 30% of max size
    const targetFree = MAX_CACHE_SIZE_BYTES * 0.3;
    let freedSize = 0;

    // Collect all items with their timestamps
    const items = [];

    for (const storeName of [STORES.BINDERS, STORES.COLLECTION_PAGES]) {
      const storeItems = await new Promise((resolve) => {
        const transaction = db.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);
        const request = store.getAll();

        request.onsuccess = () => {
          const records = request.result || [];
          resolve(
            records.map((r) => ({
              store: storeName,
              key: r.cacheKey || r.pageKey,
              fetchedAt: r.fetchedAt || 0,
              size: JSON.stringify(r).length * 2,
            }))
          );
        };

        request.onerror = () => resolve([]);
      });
      items.push(...storeItems);
    }

    // Sort by fetchedAt (oldest first)
    items.sort((a, b) => a.fetchedAt - b.fetchedAt);

    // Evict until we've freed enough
    for (const item of items) {
      if (freedSize >= targetFree) break;

      await new Promise((resolve) => {
        const transaction = db.transaction([item.store], 'readwrite');
        const store = transaction.objectStore(item.store);
        const request = store.delete(item.key);
        request.onsuccess = () => {
          console.log(`[Cache] Evicted: ${item.key}`);
          freedSize += item.size;
          resolve();
        };
        request.onerror = () => resolve();
      });
    }

    console.log(`[Cache] Eviction complete, freed ${(freedSize / 1024 / 1024).toFixed(2)}MB`);
  } catch (error) {
    console.error('[Cache] Error during eviction:', error);
  }
}

/**
 * Clear cached pages for a specific collection
 * Used when re-initializing collection data to prevent stale pages
 * from a previous session interfering with the current load
 * @param {string} collectionKey - Collection key from getCacheKey()
 * @returns {Promise<void>}
 */
export async function clearCollectionPageCache(collectionKey) {
  const db = await getDB();
  if (!db) return;

  return new Promise((resolve) => {
    try {
      const transaction = db.transaction([STORES.COLLECTION_PAGES], 'readwrite');
      const store = transaction.objectStore(STORES.COLLECTION_PAGES);
      const index = store.index('collectionKey');
      const request = index.openCursor(IDBKeyRange.only(collectionKey));

      let deleted = 0;
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          cursor.delete();
          deleted++;
          cursor.continue();
        } else {
          if (deleted > 0) {
            console.log(`[Cache] Cleared ${deleted} cached pages for: ${collectionKey}`);
          }
          resolve();
        }
      };

      request.onerror = () => {
        console.error('[Cache] Error clearing collection page cache:', request.error);
        resolve();
      };
    } catch (error) {
      console.error('[Cache] Error in clearCollectionPageCache:', error);
      resolve();
    }
  });
}

/**
 * Clear all cached data
 * @returns {Promise<void>}
 */
export async function clearCache() {
  const db = await getDB();
  if (!db) return;

  const storeNames = [
    STORES.BINDERS,
    STORES.COLLECTION_PAGES,
    STORES.COLLECTION_META,
    STORES.METADATA,
  ];

  for (const storeName of storeNames) {
    try {
      await new Promise((resolve, reject) => {
        const transaction = db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error(`[Cache] Error clearing ${storeName}:`, error);
    }
  }

  console.log('[Cache] All cache cleared');
}

/**
 * Get cache statistics
 * @returns {Promise<Object>}
 */
export async function getCacheStats() {
  const db = await getDB();
  if (!db) {
    return {
      available: false,
      totalSizeBytes: 0,
      totalSizeMB: '0.00',
      maxSizeMB: MAX_CACHE_SIZE_MB,
      binderCount: 0,
      pageCount: 0,
      collectionCount: 0,
    };
  }

  const totalSize = await estimateCacheSize();

  const counts = {};
  for (const storeName of [STORES.BINDERS, STORES.COLLECTION_PAGES, STORES.COLLECTION_META]) {
    counts[storeName] = await new Promise((resolve) => {
      const transaction = db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(0);
    });
  }

  return {
    available: true,
    totalSizeBytes: totalSize,
    totalSizeMB: (totalSize / 1024 / 1024).toFixed(2),
    maxSizeMB: MAX_CACHE_SIZE_MB,
    usagePercent: ((totalSize / MAX_CACHE_SIZE_BYTES) * 100).toFixed(1),
    binderCount: counts[STORES.BINDERS],
    pageCount: counts[STORES.COLLECTION_PAGES],
    collectionCount: counts[STORES.COLLECTION_META],
  };
}

/**
 * Check if IndexedDB cache is available
 * @returns {boolean}
 */
export function isCacheAvailable() {
  return idbAvailable;
}

// Debug utilities (console-only; declared on Window in src/lib/globals.d.ts)
export const debugCache = {
  stats: getCacheStats,
  clear: clearCache,
  clearCollection: clearCollectionPageCache,
  evict: () => performEvictionIfNeeded(true),
  isAvailable: () => idbAvailable,
  getCachedBinder,
  getCachedCollectionPage,
  getCollectionMeta,
};
if (typeof window !== 'undefined') {
  window.debugCache = debugCache;
}
